import { useEditor } from "@grapesjs/react";
import { RefreshCcw01 } from "@hl/base-components/lib/assets/icons/HDS";
import {
  ActionIcon,
  Box,
  Button,
  Group,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { Trait, TraitProperties } from "grapesjs";

import { ColorPropertyField } from "../property-fields/ColorPropertyField";
import { NumberPropertyField } from "../property-fields/NumberPropertyField";
import { RadioPropertyField } from "../property-fields/RadioPropertyField";
import { SelectPropertyField } from "../property-fields/SelectPropertyField";
import { TextPropertyField } from "../property-fields/TextPropertyField";

export type ExtendedTraitProperties = Partial<
  TraitProperties & {
    units?: string[];
  }
>;

interface StylePropertyFieldProps extends React.HTMLProps<HTMLDivElement> {
  trait: Trait;
}

export const CustomTraitPropertyField = ({
  trait,
}: // ...rest
StylePropertyFieldProps) => {
  const editor = useEditor();
  const traitProperties = trait.props() as ExtendedTraitProperties;
  const type = traitProperties.type;

  const handleOnChange = (value: string | null) => {
    if (value == null) return;
    // console.log("🍋 Custom Trait handleChange", { value }, trait);

    // Special case for image src since just changing the attribute
    // doesn't update the image correctly in grapesjs.
    // Technically a page refresh fixes it, but this is a workaround.
    if (trait.id === "src") {
      const selectedComponent = editor.getSelected();
      if (selectedComponent?.attributes.type === "image") {
        selectedComponent.set("src", value);
      }
    }
    trait.setValue(value);
  };

  const handleOnClick = () => {
    const command = trait.get("command");
    if (command) {
      typeof command === "string"
        ? editor.runCommand(command)
        : command(editor, trait);
    }
  };

  const inputProps = {
    type: traitProperties.type,
    value: traitProperties.value,
    defaultValue: traitProperties.default,
    hasValue: traitProperties.value != null,
    onChange: handleOnChange,
    onClick: handleOnClick,
  };

  console.log("🍋 CustomTraitPropertyField", {
    trait,
    traitProperties,
    inputProps,
  });

  let inputToRender = (
    <div>
      unknown ${type} ${trait.getLabel()} ${trait.getType()}
    </div>
  );

  switch (type) {
    case "text":
      inputToRender = <TextPropertyField {...inputProps} />;
      break;

    case "number": {
      inputToRender = (
        <NumberPropertyField
          {...inputProps}
          unit={traitProperties.unit ?? ""}
          units={traitProperties.units ?? []}
        />
      );
      break;
    }

    case "radio":
      {
        inputToRender = (
          <RadioPropertyField {...inputProps} options={trait.getOptions()} />
        );
      }
      break;
    case "select":
      {
        inputToRender = (
          <SelectPropertyField {...inputProps} options={trait.getOptions()} />
        );
      }
      break;
    case "color":
      {
        inputToRender = <ColorPropertyField {...inputProps} />;
      }
      break;

    case "button":
      {
        inputToRender = (
          <Button fullWidth onClick={inputProps.onClick}>
            {trait.getLabel()}
          </Button>
        );
      }
      break;

    case "list":
      {
        try {
          const value = JSON.parse(traitProperties.value);
          inputToRender = (
            <ListPropertyField
              label={trait.getLabel()}
              value={value}
              onChange={(value) => {
                console.log("new value", value);
                trait.setValue(JSON.stringify(value));
              }}
            />
          );
        } catch (error) {
          console.log(error);
        }
      }
      break;
  }

  return (
    <Box>
      <Stack spacing={0}>
        <Group noWrap position="apart" mih={24} pr={0}>
          <Stack spacing={0}>
            <Text c={"dimmed"} size={"sm"}>
              {trait.getLabel()}
            </Text>
            <Text c={"dimmed"} size={"xs"}>
              {type} - [{trait.getId()}]
            </Text>
          </Stack>

          {/* <Group spacing={"xs"}>
            {canClear && (
              <ActionIcon onClick={() => prop.clear()} size={18} c={"gray.5"}>
                <RefreshCcw01 />
              </ActionIcon>
            )}

            {type === "stack" && (
              <ActionIcon
                onClick={() => (prop as PropertyStack).addLayer({}, { at: 0 })}
                size={18}
                c={"green.6"}
              >
                <PlusCircle />
              </ActionIcon>
            )}
          </Group> */}
        </Group>

        <Box pl={0}>{inputToRender}</Box>
      </Stack>
    </Box>
  );
};

const ListPropertyField = (props: {
  label: string;
  value: string[];
  onChange: (value: string[]) => void;
}) => {
  const { label, value, onChange } = props;

  console.log("🍋 ListPropertyField", { label, value });

  return (
    <Box>
      <Text>{label}</Text>
      <Stack spacing={0}>
        {value.map((item, index) => (
          <Box key={index}>
            <Group noWrap position="apart">
              <TextInput
                value={item}
                onChange={(event) => {
                  const newValue = [...value];
                  newValue[index] = event.currentTarget.value;
                  onChange(newValue);
                }}
              />
              <ActionIcon
                onClick={() => {
                  const newValue = [...value];
                  newValue.splice(index, 1);
                  onChange(newValue);
                }}
              >
                <RefreshCcw01 />
              </ActionIcon>
            </Group>
          </Box>
        ))}
        <Button
          onClick={() => {
            onChange([...value, ""]);
          }}
        >
          Add item
        </Button>
      </Stack>
    </Box>
  );
};
